import { TECHNOLOGY_TAB_STACK_LAYOUT_BREAKPOINT_PX } from './technology-container-query.constants';

const DEFAULT_HEADER_HEIGHT = 152;
const HEADER_CONTENT_GAP = 32;

type ScrollToAnchorParams = {
  anchorElement: HTMLElement | null;
  behavior?: ScrollBehavior;
  stackedBehavior?: 'anchor' | 'top';
};

/**
 * Scrolls the window to the top of the page.
 */
export function scrollToTop(behavior: ScrollBehavior = 'smooth'): void {
  window.scrollTo({ behavior, top: 0 });
}

function getTopOffset(): number {
  const root = document.documentElement;
  const style = getComputedStyle(root);

  const headerElement = document.querySelector('[data-tech-header="true"]') as HTMLElement | null;
  const measuredHeaderHeight = headerElement?.offsetHeight;

  const headerHeightValue = style.getPropertyValue('--header-height').trim();
  const headerHeight =
    measuredHeaderHeight ?? (headerHeightValue ? Number.parseFloat(headerHeightValue) : DEFAULT_HEADER_HEIGHT);

  const bannerHeightValue = style.getPropertyValue('--impersonation-banner-height').trim();
  const bannerHeight = bannerHeightValue ? Number.parseFloat(bannerHeightValue) : 0;

  return headerHeight + bannerHeight;
}

function isStackedTechnologyLayout(anchorElement: HTMLElement): boolean {
  const technologyContainer = anchorElement.closest('[data-technology-container="true"]') as HTMLElement | null;
  const availableWidth = technologyContainer?.clientWidth ?? window.innerWidth;
  return availableWidth < TECHNOLOGY_TAB_STACK_LAYOUT_BREAKPOINT_PX;
}

function scrollToElementTop(element: HTMLElement, behavior: ScrollBehavior): void {
  const topOffset = getTopOffset() + HEADER_CONTENT_GAP;
  const elementTop = element.getBoundingClientRect().top + window.scrollY;
  const scrollTop = Math.max(elementTop - topOffset, 0);

  window.scrollTo({ behavior, top: scrollTop });
}

/**
 * Responsive scroll behavior for technology tabs.
 * - Stacked layout (<800px available): scroll to nav anchor, keeping trigger visible
 * - Split layout (>=800px): keep legacy scroll-to-top behavior
 */
export function scrollToNavOrTop(params: ScrollToAnchorParams): void {
  const { anchorElement, behavior = 'smooth', stackedBehavior = 'anchor' } = params;

  if (!anchorElement) {
    scrollToTop(behavior);
    return;
  }

  if (isStackedTechnologyLayout(anchorElement)) {
    if (stackedBehavior === 'top') {
      scrollToTop(behavior);
      return;
    }
    scrollToElementTop(anchorElement, behavior);
    return;
  }

  scrollToTop(behavior);
}
